import React, { useState } from 'react';
import classnames from 'classnames';

import {
  Menu,
  ButtonBase,
  Divider,
  MenuList,
  IconButton,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, MenuItem, Translate } from '@hydra/common/components';
import { useUserContext } from '@hydra/common/context';

import {
  useAuthenticationRoutes,
  useContactSupport,
  useI18n,
  useFeatureFlags
} from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  accountDropdownEmail: {
    fontSize: `${theme.typography.fontSizeSmall}px !important`,
    fontWeight: theme.typography.fontWeightLight,
    width: '100%',
    fontStyle: 'italic !important'
  },
  smUpHidden: {
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  }
}));

interface IRenderTriggerProps {
  className: string
  onClick: React.MouseEventHandler
}

interface IAccountDropdownProps {
  renderTrigger?: (props: IRenderTriggerProps) => React.ReactNode
}

function defaultRenderTrigger (props: IRenderTriggerProps) {
  return (
    <IconButton {...props}>
      <Icon type='user' />
    </IconButton>
  );
}

export function AccountDropdown ({
  renderTrigger = defaultRenderTrigger
}: IAccountDropdownProps) {
  const { user } = useUserContext();
  const classes = useStyles();
  const { locale } = useI18n();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const featureFlags = useFeatureFlags();
  const { isHelpLoading, onClick } = useContactSupport(locale);
  const { signOutRoute } = useAuthenticationRoutes();

  function handleClick (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose () {
    setAnchorEl(null);
  }

  const triggerComponentProps = {
    onClick: handleClick,
    className: 'nr-account-dropdown-btn'
  };

  return (
    <>
      {renderTrigger(triggerComponentProps)}
      <Menu
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuList disablePadding>
          <MenuItem>
            <Typography
              className={classnames(
                'nr-dropdown-user-email',
                classes.accountDropdownEmail
              )}
              noWrap
              component='div'
              align='center'
              variant='body2'
            >
              {user && user.email}
            </Typography>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleClose}
            href={`/${locale}/account`}
            component={ButtonBase}
          >
            <Typography noWrap component='div'>
              <Translate content='my_account' />
            </Typography>
          </MenuItem>
          <MenuItem
            href={`/${locale}/pdf-editor`}
            component={ButtonBase}
          >
            <Typography noWrap component='div'>
              <Translate content='my_templates' />
            </Typography>
          </MenuItem>
          {featureFlags.leads && (
            <MenuItem
              href={`/${locale}/leads`}
              component={ButtonBase}
            >
              <Typography noWrap component='div'>
                Leads
              </Typography>
            </MenuItem>
          )}
          <div className={classes.smUpHidden}>
            <MenuItem
              href='#'
              onClick={onClick}
              disabled={isHelpLoading}
              component={ButtonBase}
            >
              <Typography noWrap component='div'>
                <Translate content='support' />
              </Typography>
            </MenuItem>
          </div>
          {
            (user &&
              user.subscription &&
              user.subscription.plan.allowedSubusers > 0 &&
              user.masterUserId === 0
            )
              ? (
                <MenuItem
                  href={`/${locale}/account/subusers`}
                  component={ButtonBase}
                >
                  <Typography noWrap component='div'>
                    <Translate content='multiuser_menu' />
                  </Typography>
                </MenuItem>
                )
              : null
          }
          <Divider />
          <MenuItem
            href={signOutRoute}
            component={ButtonBase}
          >
            <Typography noWrap component='div' className='nr-logout-dropdown-btn'>
              <Translate content='logout' />
            </Typography>
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
}

export default React.memo(AccountDropdown);
